import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useRazorpayCheckout } from "@/lib/razorpay";
import { AppHeader } from "@/components/AppHeader";
import { LegalFooter } from "@/components/LegalLayout";
import { toast } from "sonner";
import {
  ArrowLeft,
  Check,
  Sparkles,
  Shield,
  Zap,
  MessageCircle,
  ClipboardList,
  Presentation,
  RotateCcw,
} from "lucide-react";

const FEATURES = [
  { Icon: MessageCircle, text: "Unlimited AI tutor chats (Claude Sonnet 4.5)" },
  { Icon: ClipboardList, text: "Unlimited AI-generated practice quizzes" },
  { Icon: Presentation, text: "All chapter notes + .pptx downloads" },
  { Icon: Sparkles, text: "ICSE-focused: Computer · English · Math, Class IX – XII" },
  { Icon: Shield, text: "Cancel anytime, no auto-renew surprises" },
  { Icon: Zap, text: "Priority new features as they launch" },
];

export default function PricingPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { start, submitting } = useRazorpayCheckout();
  const [plans, setPlans] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [activePlan, setActivePlan] = useState("yearly");
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    api
      .get("/billing/plans")
      .then((r) => {
        setPlans(r.data.plans);
        setConfigured(r.data.configured);
      })
      .catch(() => toast.error("Failed to load plans"));
  }, []);

  const access = user?.access;

  const onSubscribe = async (planId, opts = {}) => {
    if (!configured) {
      toast.info("Payments will be enabled shortly. Hang tight!");
      return;
    }
    setActivePlan(planId);
    await start(planId, opts);
  };

  const onRestore = async () => {
    setReconciling(true);
    try {
      const { data } = await api.post("/billing/reconcile");
      if (data.applied) {
        toast.success("Found your payment — subscription restored! 🎉");
        if (refreshUser) await refreshUser();
        navigate("/subjects");
      } else {
        toast.info(
          "No completed payment found on our side yet. If you just paid, wait a minute and try again.",
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restore. Please contact support.");
    } finally {
      setReconciling(false);
    }
  };

  const isTestKey = (
    process.env.REACT_APP_RAZORPAY_KEY_ID || ""
  ).startsWith("rzp_test_");

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-6 py-12">
        <button
          onClick={() => navigate(-1)}
          data-testid="pricing-back-btn"
          className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[hsl(var(--brimi-yellow))]/30 border border-[hsl(var(--brimi-yellow))]/50 mb-4">
            <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--brimi-coral))]" />
            <span className="text-xs font-bold uppercase tracking-wider">
              20 day free trial • cancel anytime
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-3">
            Pick the plan that fits.
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Full access to Brimi AI for every ICSE chapter. Less than a samosa
            a day.
          </p>

          {access?.status === "active" && (
            <div
              data-testid="active-plan-badge"
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--brimi-mint))]/20 border border-[hsl(var(--brimi-mint))]/40"
            >
              <Check className="w-4 h-4 text-[hsl(var(--brimi-mint))]" />
              <span className="text-sm font-bold">
                Your <span className="capitalize">{access.plan}</span> plan is
                active · {access.days_left} days left
              </span>
            </div>
          )}
          {access?.status === "trial" && (
            <div
              data-testid="trial-badge"
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--brimi-blue))]/15 border border-[hsl(var(--brimi-blue))]/30"
            >
              <Sparkles className="w-4 h-4 text-[hsl(var(--brimi-blue))]" />
              <span className="text-sm font-bold">
                Free trial: {access.days_left} day
                {access.days_left === 1 ? "" : "s"} left
              </span>
            </div>
          )}
          {access?.status === "expired" && (
            <div
              data-testid="expired-badge"
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--brimi-coral))]/15 border border-[hsl(var(--brimi-coral))]/30"
            >
              <span className="text-sm font-bold text-[hsl(var(--brimi-coral))]">
                Your trial has ended. Subscribe to keep learning.
              </span>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {plans.map((p) => {
            const isYearly = p.id === "yearly";
            return (
              <div
                key={p.id}
                data-testid={`plan-card-${p.id}`}
                className={`relative tactile-card p-7 sm:p-8 ${
                  isYearly
                    ? "border-2 border-[hsl(var(--brimi-blue))] shadow-xl shadow-[hsl(var(--brimi-blue))]/10"
                    : ""
                }`}
              >
                {p.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[hsl(var(--brimi-blue))] text-white text-[10px] font-black uppercase tracking-widest">
                    {p.badge}
                  </div>
                )}
                <div className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-2">
                  {p.name}
                </div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-5xl font-black">{p.price_label}</span>
                  <span className="text-muted-foreground font-semibold">
                    /{isYearly ? "year" : "month"}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground mb-6">{p.tagline}</div>

                <ul className="space-y-3 mb-7">
                  {FEATURES.slice(0, isYearly ? FEATURES.length : 4).map(
                    ({ Icon, text }) => (
                      <li key={text} className="flex items-start gap-3 text-sm">
                        <span
                          className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                          style={{
                            background: isYearly
                              ? "hsl(200 90% 50% / 0.15)"
                              : "hsl(10 85% 62% / 0.15)",
                            color: isYearly
                              ? "hsl(200 90% 50%)"
                              : "hsl(10 85% 62%)",
                          }}
                        >
                          <Check className="w-3 h-3" />
                        </span>
                        <span>{text}</span>
                      </li>
                    ),
                  )}
                </ul>

                <button
                  data-testid={`subscribe-${p.id}-btn`}
                  onClick={() => onSubscribe(p.id)}
                  disabled={submitting && activePlan === p.id}
                  className={`pill-btn w-full py-4 text-base font-bold ${
                    isYearly
                      ? "bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white shadow-lg shadow-[hsl(var(--brimi-blue))]/25"
                      : "bg-foreground/5 hover:bg-foreground/10 text-foreground"
                  } disabled:opacity-60`}
                >
                  {submitting && activePlan === p.id
                    ? "Opening checkout…"
                    : `Subscribe ${p.price_label}`}
                </button>
                {!configured && (
                  <div className="mt-3 text-[11px] text-center text-muted-foreground">
                    Payments setup pending — try again shortly.
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center text-sm text-muted-foreground">
          Trusted by ICSE / ISC students across India · Secure payments by
          Razorpay
        </div>

        {/* Recovery for stuck payments (e.g. money deducted but plan didn't activate
            in TWA Android wrappers when Razorpay's Custom Tab can't return cleanly). */}
        {configured && access?.status !== "active" && (
          <div className="mt-8 max-w-md mx-auto" data-testid="restore-payment-section">
            <div className="rounded-2xl bg-foreground/5 border border-border px-5 py-4 text-center">
              <div className="text-xs font-bold text-foreground mb-1">
                Already paid but plan didn&apos;t activate?
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Tap below — we&apos;ll check Razorpay for any captured payment on your
                account and unlock it instantly.
              </p>
              <button
                data-testid="restore-payment-btn"
                onClick={onRestore}
                disabled={reconciling}
                className="pill-btn bg-foreground text-background hover:bg-foreground/85 text-xs px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-60"
              >
                <RotateCcw className={`w-3.5 h-3.5 ${reconciling ? "animate-spin" : ""}`} />
                {reconciling ? "Checking…" : "Restore my payment"}
              </button>
            </div>
          </div>
        )}

        {isTestKey && configured && (
          <div className="mt-6 text-center" data-testid="test-mode-pane">
            <div className="inline-flex flex-col items-center gap-2 px-5 py-4 rounded-2xl bg-foreground/5 border border-dashed border-border max-w-md">
              <div className="text-[10px] uppercase tracking-widest font-black text-muted-foreground">
                Razorpay test mode detected
              </div>
              <p className="text-xs text-muted-foreground max-w-sm">
                Run a ₹1 smoke-test through the real Razorpay flow. Use test
                card <b>4111&nbsp;1111&nbsp;1111&nbsp;1111</b>, any future exp,
                CVV <b>123</b>, OTP <b>1234</b>.
              </p>
              <button
                data-testid="smoke-test-btn"
                onClick={() => onSubscribe("yearly", { test: true })}
                disabled={submitting}
                className="pill-btn bg-foreground text-background hover:bg-foreground/85 text-xs px-4 py-2 mt-1"
              >
                Run ₹1 smoke test
              </button>
            </div>
          </div>
        )}
      </main>
      <LegalFooter />
    </div>
  );
}
